/**
 * Tools to manage whether or not to show "first scan" modal on the Checklist view
 */
import { useMemo } from 'react'

import { AccessLevel } from '$extensionSrc/data/user/accessByPlatform'
import { useExtensionData } from '$extensionSrc/hooks/extensionStorageListener'
import { getHasAutomationsByPlatform } from '$extensionSrc/recommendations/recommendationRegistryApi'
import { PLATFORM, STORAGE_KEY } from '$extensionSrc/utils/enums'
import * as localStorage from '$extensionSrc/utils/localStorage'

import { SidebarView } from './view'

export async function dismissFirstScanModal(
  platform: PLATFORM,
  accessLevel: AccessLevel,
): Promise<void> {
  const current = await localStorage.getSingle(
    STORAGE_KEY.SIDEBAR_FIRST_SCAN_MODAL_DISMISSED,
  )

  return chrome.storage.local.set({
    [STORAGE_KEY.SIDEBAR_FIRST_SCAN_MODAL_DISMISSED]: {
      ...(current || {}),
      [platform]: {
        ...(current?.[platform] || {}),
        [accessLevel]: true,
      },
    },
  })
}

export function getPromptForFirstScanModal(
  platform: PLATFORM,
  platformAccessLevel: AccessLevel,
  platformUserId: string,
  hasCompletedFullScan: boolean,
  hasDeeplinkFeatureKey: boolean,
  showLanguageWarning: boolean,
  view: SidebarView,
): boolean | undefined {
  if (
    hasCompletedFullScan === undefined ||
    showLanguageWarning === undefined ||
    platformAccessLevel === undefined ||
    view === undefined
  ) {
    // Still loading
    return undefined
  }

  return (
    platformUserId &&
    !hasCompletedFullScan &&
    !showLanguageWarning &&
    view === 'Checklist' &&
    !hasDeeplinkFeatureKey && // Deeplink allows the user to scan a recommendation without previously dismissing the modal
    getHasAutomationsByPlatform(platform)
  )
}

export function useFirstScanModalVisible(
  platform: PLATFORM,
  platformAccessLevel: AccessLevel,
  platformUserId: string,
  hasDeeplinkFeatureKey: boolean,
  hasCompletedFullScan: boolean,
  showLanguageWarning: boolean,
  view: SidebarView,
): boolean | undefined {
  const { data, isLoaded } = useExtensionData({
    local: [STORAGE_KEY.SIDEBAR_FIRST_SCAN_MODAL_DISMISSED],
  })

  const prompt_: boolean | undefined = useMemo(
    () =>
      getPromptForFirstScanModal(
        platform,
        platformAccessLevel,
        platformUserId,
        hasDeeplinkFeatureKey,
        hasCompletedFullScan,
        showLanguageWarning,
        view,
      ),
    [
      hasCompletedFullScan,
      hasDeeplinkFeatureKey,
      platform,
      platformAccessLevel,
      platformUserId,
      showLanguageWarning,
      view,
    ],
  )

  const dismissed: boolean | undefined = useMemo(() => {
    if (!platform || !isLoaded || !data || !platformAccessLevel) {
      // Still loading
      return undefined
    }

    const d =
      data[STORAGE_KEY.SIDEBAR_FIRST_SCAN_MODAL_DISMISSED]?.[platform]?.[
        platformAccessLevel
      ]

    return d || false
  }, [data, isLoaded, platform, platformAccessLevel])

  const visible: boolean | undefined = useMemo(() => {
    if (prompt_ === undefined || dismissed === undefined) {
      return undefined
    }

    return prompt_ && !dismissed
  }, [dismissed, prompt_])

  return visible
}
